<template>
  <header
    class="bg-surface-default-low border-static-default-low flex w-full flex-col justify-between gap-16 border-b p-16 md:flex-row md:items-center"
  >
    <template v-if="isLoading">
      <div class="flex items-center gap-12">
        <RevSkeleton
          :aria-label="i18n(translations.skeletonsLabel)"
          height="48px"
          shape="rectangle"
          width="48px"
        />
        <RevSkeleton
          :aria-label="i18n(translations.skeletonsLabel)"
          height="28px"
          shape="rectangle"
          width="140px"
        />
      </div>
    </template>
    <template v-else>
      <div class="flex items-center gap-12">
        <RevIllustration
          :alt="product.title"
          class="shrink-0"
          :height="40"
          sizes="40px"
          :src="product.imageUrl"
          :width="48"
        />
        <div class="flex flex-col gap-8 md:flex-row">
          <h3 class="heading-3" data-qa="customer-request-title">
            {{ i18n(translations.title, { orderNumber: orderline.orderId }) }}
          </h3>
          <StatusTags
            :orderline
            :product-return-kind
            :product-return-state
            :repair-status
          />
        </div>
      </div>
      <div
        v-if="mostUrgentTaskForCurrentResolutionId || previousMostUrgentTask"
        class="flex flex-row items-baseline justify-between gap-12 md:gap-32"
      >
        <RevLink
          data-qa="customer-request-open-tasks-button"
          :to="{ name: ROUTE_NAMES.CUSTOMER_CARE.OPEN_TASKS }"
          :tracking="getTrackingData('open_tasks_clicked')"
        >
          {{ i18n(translations.openTasksButton) }}
        </RevLink>
        <RevLink
          data-qa="customer-request-next-task-button"
          :tracking="getTrackingData('next_task_clicked')"
          @click="handleNextTaskClick"
        >
          {{ i18n(translations.nextTaskButton) }}
        </RevLink>
      </div>
    </template>
  </header>
</template>

<script lang="ts" setup>
import { useAsyncData, useRoute } from '#imports'
import { computed, ref, watch } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevIllustration } from '@ds/components/Illustration'
import { RevLink } from '@ds/components/Link'
import { RevSkeleton } from '@ds/components/Skeleton'

import type {
  CareFolder,
  Orderline,
  Product,
  ProductReturn,
  ProductReturnState,
} from '~/api/care-folders/types/care-folder'
import type { OrderlineOpenTask } from '~/api/seller-after-sales/types'
import { ROUTE_NAMES } from '~/constants/routes'
import {
  fetchAndGoToNextTask,
  getMostUrgentTask,
} from '~/scopes/customer-care/components/request.helpers'
import { useFormatDeadline } from '~/scopes/customer-care/pages/customer-care/composables/useFormatDeadline'

import translations from './Header.translations'
import StatusTags from './components/StatusTags/StatusTags.vue'

const $httpFetch = useHttpFetch()

const i18n = useI18n()
const route = useRoute()
const { openErrorToast } = useTheToast()

const props = defineProps<{
  customerRequestId: number
  orderline: Orderline
  product: Product
  productReturnKind?: ProductReturn['kind']
  productReturnState: ProductReturnState
  repairStatus: CareFolder['repairStatus']
  isLoading: boolean
}>()

const resolutionId = route.query.resolution

const { data: requests, refresh } = useAsyncData(
  () => {
    if (!props.isLoading) {
      return $httpFetch(
        '/seller-after-sales/api/v1/orderlines/:orderlineId/requests',
        {
          pathParams: {
            orderlineId: props.orderline.orderlineId,
          },
        },
      )
    }

    return Promise.resolve(null)
  },
  {
    watch: [() => props.isLoading],
  },
)

const previousMostUrgentTask = ref<OrderlineOpenTask | null>(null)

const mostUrgentTaskForCurrentResolutionId = computed(() => {
  if (requests.value === null) return null

  return getMostUrgentTask(requests.value, resolutionId?.toString() || null)
})

function getTrackingData(name: string) {
  return {
    name,
    pageType: ROUTE_NAMES.CUSTOMER_CARE.CUSTOMER_REQUEST,
    zone: 'asp_navigation_menu',
    customerRequestId: props.customerRequestId,
    productReturnState: props.productReturnState,
    mostUrgentDeadline: mostUrgentTaskForCurrentResolutionId.value
      ? useFormatDeadline(
          mostUrgentTaskForCurrentResolutionId.value.deadline,
          mostUrgentTaskForCurrentResolutionId.value.id,
        )
      : '',
    mostUrgentTaskType: mostUrgentTaskForCurrentResolutionId.value?.type || '',
  }
}

async function handleNextTaskClick() {
  refresh()
  try {
    fetchAndGoToNextTask(
      mostUrgentTaskForCurrentResolutionId.value?.id ??
        previousMostUrgentTask.value?.id,
    )
  } catch {
    openErrorToast()
  }
}

watch(requests, async () => {
  if (mostUrgentTaskForCurrentResolutionId.value) {
    previousMostUrgentTask.value = mostUrgentTaskForCurrentResolutionId.value
  }
})
</script>
